import { pick } from 'lodash/fp';
import { throttle } from 'lodash';
import { ActionObject } from '../actions/actionTypes';
import { ELEMENT_UPDATE } from '../elements/elementConstants';
import { UnknownAction } from 'redux';

type ActionLogObject = {
    action: UnknownAction;
};

/**
 * Returns specific properties for certain actions, so some processing can be performed before logging.
 */
const getSpecificActionLogProperties = (action: ActionObject): Record<string, unknown> | undefined => {
    switch (action.type) {
        case ELEMENT_UPDATE:
            return {
                ids: action.updates?.map(({ id }) => id),
            };
        default:
            return undefined;
    }
};

/**
 * Used to prepare action for logging, by including generically necessary information and
 * filtering out sensitive data.
 *
 * NOTE: This was moved from server/common/loggerUtils.ts so it can be shared client & server side.
 * Behaviour has been kept identical (including the use of pick with 'user._id').
 */
export const prepareActionForLogging = (inputAction: UnknownAction): ActionLogObject => {
    const sanitizedAction = pick(
        [
            'type',
            'id',
            'ids',
            'user._id',
            'deviceId',
            'sessionId',
            'channels',
            'remote',
            'source',
            'bufferFlush',
            'attempt',
        ],
        inputAction,
    );
    const additionalActionProperties = getSpecificActionLogProperties(inputAction);
    const action = additionalActionProperties
        ? Object.assign(sanitizedAction, additionalActionProperties)
        : sanitizedAction;
    return { action };
};

/**
 * Wraps a log call so repeated calls collapse to at most one per `intervalMs` — a recurring
 * failure on a hot path (every socket message, every render) would otherwise flood log storage.
 * Fires immediately on the first call, then drops calls until the interval elapses (no delayed
 * trailing-edge call, so a burst logs once, not once-plus-a-late-duplicate).
 */
export const logThrottle = <Args extends unknown[]>(log: (...args: Args) => void, intervalMs: number) =>
    throttle(log, intervalMs, { leading: true, trailing: false });
